import "./base.css"

import React, { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react"

import type { FieldError } from "react-hook-form"
import SelectStyles from "./select.module.css"
import WrapWithError from "./wrap-with-error"
import classNames from "classnames"
import { useOnClickOutside } from "../../hooks/use-on-click-outside"
import { useRequiredMark } from "./required-mark"

export type ComboboxOption = { value: string | number; label: string }

const PAGE_SIZE = 10
const TYPEAHEAD_RESET_MS = 800

type ComboboxProps = {
    options: ComboboxOption[]
    value: string | number | undefined
    onSelect: (value: string | number, index: number) => void
    id: string
    label?: string
    placeholder?: string
    required?: boolean
    width?: number | string
    height?: number
    maxHeight?: number
    autoSize?: boolean
    className?: string
    error?: FieldError
    inputRef?: React.Ref<HTMLInputElement>
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

const Combobox = ({
    options,
    value,
    onSelect,
    id,
    label,
    placeholder,
    required,
    width,
    height,
    maxHeight,
    autoSize,
    inputRef,
    inputProps,
    className,
    error,
}: ComboboxProps) => {
    const showRequiredMark = useRequiredMark()

    const listboxId = `${id}-listbox`
    const optionId = (index: number) => `${id}-option-${index}`

    const [inFocus, setInFocus] = useState(false)
    const [active, setActive] = useState<number | undefined>(
        placeholder
            ? undefined
            : Math.max(
                  options.findIndex(o => o.value === value),
                  0
              )
    )
    const blockRef = useRef<HTMLDivElement>(null)
    const optionsRef = useRef<HTMLDivElement>(null)
    const blockOnMouseOver = useRef(false)
    const typeahead = useRef({ query: "", at: 0 })

    const scrollIntoView = useCallback((index: number) => {
        const optionsEl = optionsRef.current
        if (!optionsEl) return
        const focusedEl = Array.from(optionsEl.children)[index] as HTMLElement
        if (!focusedEl) return
        const optionsRec = optionsEl.getBoundingClientRect()
        const focusedRect = focusedEl.getBoundingClientRect()
        const overScroll = focusedEl.offsetHeight / 3
        if (focusedRect.bottom + overScroll > optionsRec.bottom) {
            optionsEl.scrollTo(
                0,
                Math.min(
                    focusedEl.offsetTop + focusedEl.clientHeight - optionsEl.offsetHeight + overScroll,
                    optionsEl.scrollHeight
                )
            )
        } else if (focusedRect.top - overScroll < optionsRec.top) {
            optionsEl.scrollTo(0, Math.max(focusedEl.offsetTop - overScroll, 0))
        }
    }, [])

    const focusValue = useCallback(
        (direction: "next" | "previous") => {
            setActive(prev => {
                const previous = prev ?? 0
                const index =
                    direction === "next"
                        ? previous + 1 >= options.length
                            ? 0
                            : previous + 1
                        : previous - 1 < 0
                          ? options.length - 1
                          : previous - 1
                scrollIntoView(index)
                return index
            })
        },
        [options, scrollIntoView]
    )

    const moveTo = useCallback(
        (index: number) => {
            setActive(index)
            scrollIntoView(index)
        },
        [scrollIntoView]
    )

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.defaultPrevented) return
            blockOnMouseOver.current = true
            const selectActive = () => typeof active === "number" && onSelect(options[active].value, active)
            switch (event.key) {
                case "ArrowUp":
                    focusValue("previous")
                    break
                case "ArrowDown":
                    if (!inFocus) {
                        setInFocus(true)
                        break
                    }
                    focusValue("next")
                    break
                case "Home":
                case "End":
                case "PageUp":
                case "PageDown": {
                    if (!inFocus) {
                        setInFocus(true)
                        break
                    }
                    if (!options.length) break
                    const last = options.length - 1
                    const from = active ?? 0
                    moveTo(
                        event.key === "Home"
                            ? 0
                            : event.key === "End"
                              ? last
                              : event.key === "PageUp"
                                ? Math.max(from - PAGE_SIZE, 0)
                                : Math.min(from + PAGE_SIZE, last)
                    )
                    break
                }
                case "Enter":
                case " ":
                    if (!inFocus) {
                        setInFocus(true)
                        break
                    }
                    selectActive()
                    setInFocus(false)
                    break
                case "Tab":
                    if (!inFocus) return
                    selectActive()
                    setInFocus(false)
                    return
                case "Escape":
                    setInFocus(false)
                    break
                default: {
                    // Printable keys extend a short-lived buffer, so typing "1978" jumps to that year
                    // instead of making four unrelated single-character jumps. Values are matched as
                    // well as labels, so "1" finds day 1 even though its label is zero-padded "01.".
                    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return
                    const now = Date.now()
                    const query =
                        (now - typeahead.current.at < TYPEAHEAD_RESET_MS ? typeahead.current.query : "") +
                        event.key.toLowerCase()
                    typeahead.current = { query, at: now }
                    const index = options.findIndex(
                        o => o.label.toLowerCase().startsWith(query) || String(o.value).toLowerCase().startsWith(query)
                    )
                    if (index < 0) return
                    if (!inFocus) setInFocus(true)
                    moveTo(index)
                    break
                }
            }
            event.preventDefault()
        },
        [active, options, onSelect, focusValue, inFocus, moveTo]
    )

    useEffect(() => {
        if (!inFocus) return
        const index = options.findIndex(o => o.value === value)
        if (index < 0) return
        moveTo(index)
    }, [inFocus, options, value, moveTo])

    useOnClickOutside(() => setInFocus(false), inFocus ? [blockRef] : [])

    const selectedOption = options.find(o => o.value === value)
    const selectedLabel = selectedOption?.label ?? ""

    const handleClick = (val: string | number, index: number) => {
        onSelect(val, index)
        setActive(index)
        setInFocus(false)
    }

    return (
        <div
            className={classNames(
                SelectStyles.block,
                {
                    [SelectStyles.error]: error,
                    error: error,
                },
                className
            )}
            style={{ width: width }}
            ref={blockRef}
        >
            <WrapWithError error={error} id={id}>
                <div
                    className={classNames(SelectStyles.select, {
                        [SelectStyles.open]: inFocus,
                        [SelectStyles.empty]: !selectedLabel,
                    })}
                >
                    <input
                        type="text"
                        readOnly
                        ref={inputRef}
                        id={id}
                        value={selectedLabel}
                        placeholder={placeholder}
                        onFocus={() => setInFocus(true)}
                        onClick={() => setInFocus(true)}
                        onKeyDown={onKeyDown}
                        className="input"
                        style={{ height: height }}
                        size={autoSize ? selectedLabel.length || undefined : undefined}
                        required={required}
                        role="combobox"
                        aria-haspopup="listbox"
                        aria-expanded={inFocus}
                        aria-controls={listboxId}
                        aria-autocomplete="none"
                        aria-activedescendant={inFocus && typeof active === "number" ? optionId(active) : undefined}
                        aria-invalid={!!error}
                        aria-errormessage={"err-" + id}
                        {...inputProps}
                    />
                    {label && (
                        <label className="label" htmlFor={id}>
                            {label}
                            {required && showRequiredMark && <span className="required"> *</span>}
                        </label>
                    )}
                    <i className="lavita-icon" aria-hidden="true">
                        
                    </i>
                </div>
                <div className={SelectStyles.options}>
                    <div ref={optionsRef} id={listboxId} role="listbox" style={{ maxHeight }}>
                        {options.map((option, index) => (
                            <button
                                type="button"
                                key={option.value}
                                id={optionId(index)}
                                role="option"
                                aria-selected={option.value === value}
                                onClick={() => handleClick(option.value, index)}
                                onFocus={() => {
                                    blockOnMouseOver.current = false
                                }}
                                onMouseOver={() => {
                                    if (!blockOnMouseOver.current) setActive(index)
                                }}
                                onMouseMove={() => {
                                    blockOnMouseOver.current = false
                                }}
                                className={classNames({ [SelectStyles.selectActive]: active === index })}
                                style={{ height: height }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            </WrapWithError>
        </div>
    )
}

export default Combobox
